import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Stack,
  Typography,
  Divider,
  Paper,
  IconButton,
  InputAdornment,
  Fade,
  Slide,
  Grow,
  Zoom,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import { createNode } from "../../api/nodeAPI";
import { getRegion } from "../../api/regionAPI";
import { useTranslation } from "react-i18next";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getRegionLevel } from "../regions/RegionGrid";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Dialog transition components
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ZoomTransition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

// Leaflet default marker icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Map click event handler
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    },
  });
  return null;
}

// Map center and zoom controller
function MapController({ center, zoom }) {
  const map = useMap();
  React.useEffect(() => {
    if (center) {
      map.flyTo(center, zoom, { duration: 1 });
    }
  }, [center, zoom, map]);
  return null;
}

// Koordinattan ülke kodu (ISO alpha-2) tespiti (Nominatim reverse)
async function reverseGeocodeCountryCode(lat, lng) {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      { headers: { "Accept-Language": "en" } }
    );
    const data = await response.json();
    const cc = data?.address?.country_code;
    return cc ? cc.toUpperCase() : null;
  } catch (e) {
    return null;
  }
}

// İl/ilçe adı eşleştirme için normalize (Türkçe karakterleri ve boşluk/işaretleri sadeleştir)
function normalizePlaceName(s) {
  return (s || "")
    .toString()
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export default function PlaceControls({ onPlacesUpdate }) {
  const { t } = useTranslation();

  const [openAddDialog, setOpenAddDialog] = React.useState(false);
  const [regions, setRegions] = React.useState([]);
  const [level2Regions, setLevel2Regions] = React.useState([]);
  const [level3Regions, setLevel3Regions] = React.useState([]);
  const [selectedLevel2Region, setSelectedLevel2Region] = React.useState(0);
  const [newPlace, setNewPlace] = React.useState({
    idNode: 0,
    name: "",
    code: "",
    address: "",
    city: "",
    town: "",
    group1: "",
    group2: "",
    group3: "",
    region_idRegion: 0,
    propStr1: "",
    propStr2: "",
    propStr3: "",
    propStr4: "",
    propStr5: "",
    propInt1: 0,
    propInt2: 0,
    propInt3: 0,
    propInt4: 0,
    propInt5: 0,
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isCreating, setIsCreating] = React.useState(false);

  // Location selection states
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");

  // Map modal states
  const [openMapDialog, setOpenMapDialog] = React.useState(false);
  const [mapCenter, setMapCenter] = React.useState({ lat: 20, lng: 0 });
  const [mapZoom, setMapZoom] = React.useState(2);
  const [tempLocation, setTempLocation] = React.useState(null);

  // Search states
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isSearching, setIsSearching] = React.useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = React.useState(false);

  // Ülke → İl → İlçe seçimi (country-state-city, dialog açılınca lazy-load)
  const cscRef = React.useRef(null);
  const [countries, setCountries] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [selectedState, setSelectedState] = React.useState(null);
  const [selectedCity, setSelectedCity] = React.useState(null);
  // Sayfa açılışında konumdan önceden tespit edilen ülke/koordinat
  const [detectedCountry, setDetectedCountry] = React.useState(null);
  const [detectedCoords, setDetectedCoords] = React.useState(null);

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const allRegions = await getRegion();
        setRegions(allRegions);

        const level2 = allRegions.filter(
          (region) => getRegionLevel(region, allRegions) === 2
        );
        setLevel2Regions(level2);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    loadData();
  }, []);

  // Sayfa açılışında: ülke verisini yükle + konum iznini iste, ülkeyi önceden tespit et
  React.useEffect(() => {
    let active = true;
    const init = async () => {
      try {
        if (!cscRef.current) {
          const mod = await import("country-state-city");
          // webpack CJS/ESM interop: named export yoksa default'a düş
          cscRef.current = mod.Country ? mod : mod.default || mod;
        }
        if (!active) return;
        const allCountries = cscRef.current.Country.getAllCountries();
        setCountries(allCountries);

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude: lat, longitude: lng } = position.coords;
              const code = await reverseGeocodeCountryCode(lat, lng);
              if (!active) return;
              const country = code
                ? allCountries.find((c) => c.isoCode === code)
                : null;
              setDetectedCountry(country || null);
              setDetectedCoords({ lat, lng });
            },
            () => {
              // İzin yok/başarısız → tespit edilmez, ülke boş kalır
            },
            { timeout: 10000, maximumAge: 60000 }
          );
        }
      } catch (e) {
        console.error("country-state-city yüklenemedi:", e);
      }
    };
    init();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (selectedLevel2Region && regions.length > 0) {
      const level3 = regions.filter(
        (region) =>
          getRegionLevel(region, regions) === 3 &&
          region.parentRegion === selectedLevel2Region
      );
      setLevel3Regions(level3);
    } else {
      setLevel3Regions([]);
    }
  }, [selectedLevel2Region, regions]);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Sayfa açılışında konumdan tespit edilen ülke/koordinatı anında uygula
    if (detectedCountry && !selectedCountry) {
      applyCountry(detectedCountry, false);
    }
    if (detectedCoords) {
      setMapCenter(detectedCoords);
      setMapZoom(8);
    }
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedLevel2Region(0);
    setNewPlace({
      idNode: 0,
      name: "",
      code: "",
      address: "",
      city: "",
      town: "",
      group1: "",
      group2: "",
      group3: "",
      region_idRegion: 0,
      propStr1: "",
      propStr2: "",
      propStr3: "",
      propStr4: "",
      propStr5: "",
      propInt1: 0,
      propInt2: 0,
      propInt3: 0,
      propInt4: 0,
      propInt5: 0,
    });
    // Reset location states
    setLatitude("");
    setLongitude("");
    setMapCenter({ lat: 20, lng: 0 });
    setMapZoom(2);
    setTempLocation(null);
    // Ülke/İl/İlçe seçimlerini sıfırla
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setStates([]);
    setCities([]);
  };

  // Open map modal
  const handleOpenMapDialog = () => {
    // If coordinates exist, position map there
    if (latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
        setMapZoom(15);
        setTempLocation({ lat, lng });
      }
    }
    setOpenMapDialog(true);
  };

  // Close map modal
  const handleCloseMapDialog = () => {
    setOpenMapDialog(false);
    setTempLocation(null);
    setSearchQuery("");
  };

  // When location is selected on map
  const handleMapLocationSelect = async (latlng) => {
    setTempLocation(latlng);
    // Perform reverse geocoding
    await reverseGeocode(latlng.lat, latlng.lng);
  };

  // Address search with Nominatim
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}${
          selectedCountry
            ? `&countrycodes=${selectedCountry.isoCode.toLowerCase()}`
            : ""
        }&limit=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        setMapCenter({ lat, lng });
        setMapZoom(15);
        setTempLocation({ lat, lng });

        // Get detailed info with reverse geocoding
        await reverseGeocode(lat, lng);
      } else {
        setSnackbar({
          open: true,
          message: t("places.dialogs.addPlace.addressNotFound") || "Adres bulunamadı",
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      setSnackbar({
        open: true,
        message: t("places.dialogs.addPlace.searchError") || "Arama sırasında hata oluştu",
        severity: "error",
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Haritadan seçilen koordinatın ülke/il/ilçesini dropdown'lara yansıt (ters eşleştirme)
  const applyReverseLocation = (address) => {
    if (!address || !cscRef.current) return;
    const { Country, State, City } = cscRef.current;
    const cc = address.country_code
      ? address.country_code.toUpperCase()
      : null;
    if (!cc) return;
    const countryList = countries.length
      ? countries
      : Country.getAllCountries();
    const country = countryList.find((c) => c.isoCode === cc);
    if (!country) return;

    // İl (state) eşleştir
    const stateList = State.getStatesOfCountry(country.isoCode);
    const stateCandidates = [
      address.state,
      address.province,
      address.region,
      address.state_district,
    ]
      .filter(Boolean)
      .map(normalizePlaceName);
    const matchedState =
      stateList.find((s) =>
        stateCandidates.includes(normalizePlaceName(s.name))
      ) ||
      stateList.find((s) => {
        const n = normalizePlaceName(s.name);
        return stateCandidates.some((c) => c && (n.includes(c) || c.includes(n)));
      }) ||
      null;

    // İlçe (city) eşleştir
    let cityList = [];
    let matchedCity = null;
    if (matchedState) {
      cityList = City.getCitiesOfState(country.isoCode, matchedState.isoCode);
      const cityCandidates = [
        address.county,
        address.city_district,
        address.district,
        address.town,
        address.suburb,
        address.municipality,
        address.city,
        address.village,
      ]
        .filter(Boolean)
        .map(normalizePlaceName);
      matchedCity =
        cityList.find((ci) =>
          cityCandidates.includes(normalizePlaceName(ci.name))
        ) ||
        cityList.find((ci) => {
          const n = normalizePlaceName(ci.name);
          return cityCandidates.some(
            (c) => c && (n.includes(c) || c.includes(n))
          );
        }) ||
        null;
    }

    setSelectedCountry(country);
    setStates(stateList);
    setSelectedState(matchedState);
    setCities(cityList);
    setSelectedCity(matchedCity);
    setNewPlace((prev) => ({
      ...prev,
      city: matchedState ? matchedState.name : "",
      town: matchedCity ? matchedCity.name : "",
    }));
  };

  // Reverse geocoding - get address info from coordinates
  const reverseGeocode = async (lat, lng) => {
    setIsReverseGeocoding(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      const data = await response.json();

      if (data) {
        // Koordinat + açık adres doldurulur; ülke/il/ilçe ters eşleştirme ile dropdown'lara yansır.
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        const fullAddress = data.display_name || "";
        if (fullAddress) {
          setNewPlace((prev) => ({ ...prev, address: fullAddress }));
        }
        if (data.address) {
          applyReverseLocation(data.address);
        }
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    } finally {
      setIsReverseGeocoding(false);
    }
  };

  // Confirm selected location from map
  const handleConfirmLocation = () => {
    if (tempLocation) {
      setLatitude(tempLocation.lat.toFixed(6));
      setLongitude(tempLocation.lng.toFixed(6));
    }
    handleCloseMapDialog();
  };

  // Location fetching state
  const [isGettingLocation, setIsGettingLocation] = React.useState(false);

  // Get current location from browser (inside map modal)
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSnackbar({
        open: true,
        message:
          t("places.dialogs.addPlace.geolocationNotSupported") ||
          "Tarayıcınız konum servisini desteklemiyor",
        severity: "error",
      });
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        const newLocation = { lat, lng };
        setTempLocation(newLocation);
        setMapCenter(newLocation);
        setMapZoom(15);

        // Perform reverse geocoding
        await reverseGeocode(lat, lng);

        setIsGettingLocation(false);
        setSnackbar({
          open: true,
          message:
            t("places.dialogs.addPlace.locationObtained") ||
            "Konumunuz başarıyla alındı",
          severity: "success",
        });
      },
      (error) => {
        setIsGettingLocation(false);
        let errorMessage =
          t("places.dialogs.addPlace.locationError") || "Konum alınamadı";
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage =
            t("places.dialogs.addPlace.locationPermissionDenied") ||
            "Konum izni reddedildi";
        }
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // Ülke seçimi: İl listesini yükle, alt seçimleri sıfırla, (isteğe bağlı) haritayı ülkeye getir
  const applyCountry = (country, recenter = true) => {
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    setCities([]);
    setNewPlace((prev) => ({ ...prev, city: "", town: "" }));
    if (country && cscRef.current) {
      setStates(cscRef.current.State.getStatesOfCountry(country.isoCode));
      if (recenter) {
        const lat = parseFloat(country.latitude);
        const lng = parseFloat(country.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter({ lat, lng });
          setMapZoom(5);
        }
      }
    } else {
      setStates([]);
    }
  };

  const handleCountryChange = (event, country) => applyCountry(country, true);

  // İl seçimi: İlçe listesini yükle, city = il adı, haritayı ile getir
  const handleStateChange = (event, state) => {
    setSelectedState(state);
    setSelectedCity(null);
    setNewPlace((prev) => ({ ...prev, city: state ? state.name : "", town: "" }));
    if (state && selectedCountry && cscRef.current) {
      setCities(
        cscRef.current.City.getCitiesOfState(selectedCountry.isoCode, state.isoCode)
      );
      const lat = parseFloat(state.latitude);
      const lng = parseFloat(state.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
        setMapZoom(8);
      }
    } else {
      setCities([]);
    }
  };

  // İlçe seçimi: town = ilçe adı, koordinat + marker
  const handleCityChange = (event, city) => {
    setSelectedCity(city);
    setNewPlace((prev) => ({ ...prev, town: city ? city.name : "" }));
    if (city) {
      const lat = parseFloat(city.latitude);
      const lng = parseFloat(city.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
        setMapZoom(12);
        setTempLocation({ lat, lng });
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
      }
    }
  };

  // Dialog açıkken konum tespiti tamamlanırsa (kullanıcı henüz seçmediyse) uygula
  React.useEffect(() => {
    if (openAddDialog && detectedCountry && !selectedCountry) {
      applyCountry(detectedCountry, false);
      if (detectedCoords) {
        setMapCenter(detectedCoords);
        setMapZoom(8);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddDialog, detectedCountry]);

  const handleCreatePlace = async () => {
    if (isCreating) return;
    if (
      !newPlace.code ||
      !newPlace.name ||
      !newPlace.region_idRegion ||
      !newPlace.address ||
      !newPlace.city
    ) {
      setSnackbar({
        open: true,
        message: "Tüm zorunlu alanları doldurunuz.",
        severity: "error",
      });
      return;
    }
    try {
      setIsCreating(true);
      const placeData = {
        idNode: 0,
        name: newPlace.name,
        code: newPlace.code,
        address: newPlace.address === "" ? null : newPlace.address,
        city: newPlace.city === "" ? null : newPlace.city,
        town: newPlace.town === "" ? null : newPlace.town,
        group1: newPlace.group1 === "" ? null : newPlace.group1,
        group2: newPlace.group2 === "" ? null : newPlace.group2,
        group3: newPlace.group3 === "" ? null : newPlace.group3,
        region_idRegion: newPlace.region_idRegion,
        isdeleted: 0,
        propStr1: newPlace.propStr1 === "" ? null : newPlace.propStr1,
        propStr2: newPlace.propStr2 === "" ? null : newPlace.propStr2,
        propStr3: newPlace.propStr3 === "" ? null : newPlace.propStr3,
        propStr4: newPlace.propStr4 === "" ? null : newPlace.propStr4,
        propStr5: newPlace.propStr5 === "" ? null : newPlace.propStr5,
        propInt1: newPlace.propInt1 === 0 ? null : newPlace.propInt1,
        propInt2: newPlace.propInt2 === 0 ? null : newPlace.propInt2,
        propInt3: newPlace.propInt3 === 0 ? null : newPlace.propInt3,
        propInt4: newPlace.propInt4 === 0 ? null : newPlace.propInt4,
        propInt5: newPlace.propInt5 === 0 ? null : newPlace.propInt5,
        latitude: latitude || null,
        longitude: longitude || null,
      };

      await createNode(placeData);

      setSnackbar({
        open: true,
        message: t("places.messages.placeCreated"),
        severity: "success",
      });

      handleCloseAddDialog();

      if (onPlacesUpdate) onPlacesUpdate();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || t("places.messages.errorCreatingPlace"),
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenAddDialog}
        size="small"
        sx={{ borderRadius: 2 }}
      >
        {t("places.controls.addPlace")}
      </Button>

      {/* Add Place Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={SlideTransition}
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            pb: 1,
          }}
        >
          {t("places.dialogs.addPlace.title")}
        </DialogTitle>

        <DialogContent sx={{ pt: 3, mt: 1 }}>
          {/* Required Fields Section */}
          <Grow in={openAddDialog} timeout={400}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              borderColor: "primary.main",
              borderWidth: 1.5,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              {t("places.dialogs.addPlace.requiredFields")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoFocus
                  label={t("places.dialogs.addPlace.code")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={newPlace.code}
                  onChange={(e) =>
                    setNewPlace({ ...newPlace, code: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("places.dialogs.addPlace.name")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={newPlace.name}
                  onChange={(e) =>
                    setNewPlace({ ...newPlace, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel>
                    {t("places.dialogs.addPlace.region")}
                  </InputLabel>
                  <Select
                    value={selectedLevel2Region}
                    label={t("places.dialogs.addPlace.region")}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setSelectedLevel2Region(value);
                      setNewPlace({
                        ...newPlace,
                        region_idRegion: 0,
                      });
                    }}
                  >
                    {level2Regions.map((region) => (
                      <MenuItem
                        key={region.idRegion}
                        value={region.idRegion}
                      >
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  required
                  disabled={!selectedLevel2Region}
                  size="small"
                >
                  <InputLabel>
                    {t("places.dialogs.addPlace.subRegion")}
                  </InputLabel>
                  <Select
                    value={newPlace.region_idRegion}
                    label={t("places.dialogs.addPlace.subRegion")}
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        region_idRegion: Number(e.target.value),
                      })
                    }
                  >
                    {level3Regions.map((region) => (
                      <MenuItem
                        key={region.idRegion}
                        value={region.idRegion}
                      >
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
          </Grow>

          {/* Location Section */}
          <Grow in={openAddDialog} timeout={600} style={{ transitionDelay: '100ms' }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              borderColor: "success.main",
              borderWidth: 1.5,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="success.main"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <LocationOnIcon fontSize="small" />
              {t("places.dialogs.addPlace.locationSelection") || "Konum Bilgileri"}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {/* Ülke (listeden seçim) */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={countries}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) => o.isoCode === v.isoCode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="outlined"
                      label={t("places.dialogs.addPlace.country") || "Ülke"}
                    />
                  )}
                />
              </Grid>

              {/* İl (seçilen ülkeye göre) */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={states}
                  value={selectedState}
                  onChange={handleStateChange}
                  disabled={!selectedCountry}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) => o.isoCode === v.isoCode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      variant="outlined"
                      label={t("places.dialogs.addPlace.province") || "İl"}
                    />
                  )}
                />
              </Grid>

              {/* İlçe (seçilen ile göre) */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={cities}
                  value={selectedCity}
                  onChange={handleCityChange}
                  disabled={!selectedState}
                  getOptionLabel={(o) => o?.name || ""}
                  isOptionEqualToValue={(o, v) =>
                    o.name === v.name && o.latitude === v.latitude
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("places.dialogs.addPlace.district") || "İlçe"}
                    />
                  )}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  label={t("places.dialogs.addPlace.address")}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={newPlace.address}
                  onChange={(e) =>
                    setNewPlace({ ...newPlace, address: e.target.value })
                  }
                  required
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Latitude */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("places.dialogs.addPlace.latitude") || "Enlem (Latitude)"}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="41.015137"
                />
              </Grid>

              {/* Longitude */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("places.dialogs.addPlace.longitude") || "Boylam (Longitude)"}
                  type="text"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="28.979530"
                />
              </Grid>

              {/* Select from Map Button */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="success"
                  fullWidth
                  startIcon={<MapIcon />}
                  onClick={handleOpenMapDialog}
                  sx={{ borderRadius: 2, py: 1.2, textTransform: "none" }}
                >
                  {t("places.dialogs.addPlace.selectFromMap") || "Haritadan Seç"}
                </Button>
              </Grid>
            </Grid>
          </Paper>
          </Grow>

          {/* Optional Group Fields */}
          <Grow in={openAddDialog} timeout={800} style={{ transitionDelay: '200ms' }}>
          <Accordion
            defaultExpanded={false}
            sx={{
              mb: 2,
              border: "1.5px solid",
              borderColor: "grey.400",
              borderRadius: 2,
              "&:before": { display: "none" },
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "grey.100", borderRadius: "8px 8px 0 0" }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t("places.dialogs.addPlace.optionalFields")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t("places.dialogs.addPlace.group1")}
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.group1}
                    onChange={(e) =>
                      setNewPlace({ ...newPlace, group1: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t("places.dialogs.addPlace.group2")}
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.group2}
                    onChange={(e) =>
                      setNewPlace({ ...newPlace, group2: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t("places.dialogs.addPlace.group3")}
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.group3}
                    onChange={(e) =>
                      setNewPlace({ ...newPlace, group3: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          </Grow>

          {/* Extra Details Accordion */}
          <Fade in={openAddDialog} timeout={1000} style={{ transitionDelay: '300ms' }}>
          <Accordion
            defaultExpanded={false}
            sx={{
              border: "1.5px solid",
              borderColor: "grey.400",
              borderRadius: 2,
              "&:before": { display: "none" },
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: "grey.100", borderRadius: "8px 8px 0 0" }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t("places.dialogs.addPlace.extraDetails")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Str 1"
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.propStr1}
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propStr1: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Str 2"
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.propStr2}
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propStr2: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Str 3"
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.propStr3}
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propStr3: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Str 4"
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.propStr4}
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propStr4: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Str 5"
                    type="text"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={newPlace.propStr5}
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propStr5: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6} />
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Prop Int 1"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={
                      newPlace.propInt1 === 0 ? "" : newPlace.propInt1
                    }
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propInt1:
                          e.target.value === ""
                            ? 0
                            : Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Prop Int 2"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={
                      newPlace.propInt2 === 0 ? "" : newPlace.propInt2
                    }
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propInt2:
                          e.target.value === ""
                            ? 0
                            : Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Prop Int 3"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={
                      newPlace.propInt3 === 0 ? "" : newPlace.propInt3
                    }
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propInt3:
                          e.target.value === ""
                            ? 0
                            : Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Int 4"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={
                      newPlace.propInt4 === 0 ? "" : newPlace.propInt4
                    }
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propInt4:
                          e.target.value === ""
                            ? 0
                            : Number(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prop Int 5"
                    type="number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={
                      newPlace.propInt5 === 0 ? "" : newPlace.propInt5
                    }
                    onChange={(e) =>
                      setNewPlace({
                        ...newPlace,
                        propInt5:
                          e.target.value === ""
                            ? 0
                            : Number(e.target.value),
                      })
                    }
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Button
            onClick={handleCloseAddDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleCreatePlace}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 120 }}
            disabled={isCreating}
          >
            {isCreating ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.creating") || "Creating..."}</span>
              </Stack>
            ) : (
              t("common.create")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Modal Dialog */}
      <Dialog
        open={openMapDialog}
        onClose={handleCloseMapDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={ZoomTransition}
      >
        <DialogTitle
          sx={{
            bgcolor: "success.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MapIcon />
            {t("places.dialogs.addPlace.selectFromMap") || "Haritadan Konum Seç"}
          </Box>
          <IconButton
            onClick={handleCloseMapDialog}
            sx={{ color: "white" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Search Area */}
          <Fade in={openMapDialog} timeout={500}>
          <Box sx={{ p: 2, bgcolor: "grey.100" }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("places.dialogs.addPlace.searchPlaceholder") || "Adres veya konum ara..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearch}
                      disabled={isSearching}
                      sx={{ borderRadius: 2 }}
                    >
                      {isSearching ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        t("common.search") || "Ara"
                      )}
                    </Button>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  borderRadius: 2,
                },
              }}
            />
          </Box>
          </Fade>

          {/* Map */}
          <Grow in={openMapDialog} timeout={600} style={{ transitionDelay: '100ms' }}>
          <Box sx={{ height: 400, width: "100%" }}>
            <MapContainer
              center={[mapCenter.lat, mapCenter.lng]}
              zoom={mapZoom}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapController
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={mapZoom}
              />
              <MapClickHandler onLocationSelect={handleMapLocationSelect} />
              {tempLocation && (
                <Marker position={[tempLocation.lat, tempLocation.lng]} />
              )}
            </MapContainer>
          </Box>
          </Grow>

          {/* Bottom Info Area */}
          <Fade in={openMapDialog} timeout={700} style={{ transitionDelay: '200ms' }}>
          <Box sx={{ p: 2, bgcolor: "grey.50" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                {tempLocation ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      bgcolor: "success.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "success.200",
                    }}
                  >
                    <MyLocationIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="success.dark">
                      <strong>
                        {t("places.dialogs.addPlace.selectedCoordinates") || "Seçilen Koordinatlar"}:
                      </strong>{" "}
                      {tempLocation.lat.toFixed(6)}, {tempLocation.lng.toFixed(6)}
                      {isReverseGeocoding && (
                        <CircularProgress size={12} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("places.dialogs.addPlace.mapHint") ||
                      "Haritaya tıklayarak konum seçebilirsiniz"}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  fullWidth
                  startIcon={
                    isGettingLocation ? (
                      <CircularProgress size={16} />
                    ) : (
                      <MyLocationIcon />
                    )
                  }
                  onClick={handleGetCurrentLocation}
                  disabled={isGettingLocation}
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  {isGettingLocation
                    ? t("places.dialogs.addPlace.gettingLocation") || "Konum alınıyor..."
                    : t("places.dialogs.addPlace.getCurrentLocation") || "Şuanki Konumu Al"}
                </Button>
              </Grid>
            </Grid>
          </Box>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseMapDialog}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel") || "İptal"}
          </Button>
          <Button
            onClick={handleConfirmLocation}
            color="success"
            variant="contained"
            disabled={!tempLocation}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            {t("places.dialogs.addPlace.confirmLocation") || "Konumu Onayla"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
